// src/pages/campaigns/CampaignDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink, Link } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Divider,
  Paper,
  Chip,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  useTheme,
  alpha,
  Breadcrumbs,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  BarChart as AnalyticsIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Layers as ContentIcon,
  CalendarToday as CalendarIcon,
  AutoAwesome as MagicIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  NavigateNext as NavigateNextIcon,
  Flag as MilestoneIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';

// Import custom components
import MagicCard from '../../components/ui/MagicCard';
import { Button } from '../../design-system/components/core';
import PageHeader from '../../components/ui/PageHeader';
import MagicLoading from '../../components/ui/MagicLoading';
import { EmptyState } from '../../design-system/components/core';

// Import hooks and stores
import useCampaignStore from '../../stores/campaignStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import useUIStore from '../../stores/uiStore';
import DataStateHandler from '../../components/core/data/DataStateHandler';
import CampaignBudgetTab from './CampaignBudgetTab';
import CampaignContentTab from './CampaignContentTab';
import CampaignMilestoneTab from './CampaignMilestoneTab';
import CampaignKPIsTab from './CampaignKPIsTab';

// Import the shared TabPanel component instead of using a custom one
import { TabPanel } from '../../components/ui';

// Status chip component
const StatusChip = ({ status }) => {
  // Define colors and labels based on status
  const statusConfig = {
    draft: { color: 'default', label: 'Draft' },
    planning: { color: 'info', label: 'Planning' },
    active: { color: 'success', label: 'Active' },
    paused: { color: 'warning', label: 'Paused' },
    completed: { color: 'primary', label: 'Completed' },
    cancelled: { color: 'error', label: 'Cancelled' }
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <Chip 
      label={config.label} 
      color={config.color} 
      size="small" 
      sx={{ fontWeight: 500 }}
    />
  );
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Format currency helper
const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency 
  }).format(amount);
};

// Platform icon component
const PlatformIcon = ({ platform }) => {
  switch (platform) {
    case 'facebook':
      return <FacebookIcon />;
    case 'instagram':
      return <InstagramIcon />;
    case 'twitter':
      return <TwitterIcon />;
    case 'linkedin':
      return <LinkedInIcon />;
    case 'youtube':
      return <YouTubeIcon />;
    default:
      return <CampaignIcon />;
  }
};

// Campaign detail component
const CampaignDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    currentCampaign, 
    isLoading, 
    criticalError,
    operationError,
    clearError,
    clearOperationError,
    fetchCampaign, 
    deleteCampaign,
    updateCampaignStatus,
  } = useCampaignStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  const { addNotification } = useUIStore();
  
  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  
  // Check permissions
  const canUpdate = hasPermission('campaigns', 'update');
  const canDelete = hasPermission('campaigns', 'delete');
  
  // Fetch campaign on component mount
  useEffect(() => {
    fetchCampaign(id);
  }, [fetchCampaign, id]);

  // Show operation error notifications
  useEffect(() => {
    if (operationError) {
      addNotification({
        message: operationError,
        type: 'error'
      });
      clearOperationError();
    }
  }, [operationError, addNotification, clearOperationError]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle menu open
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Handle status menu open
  const handleStatusMenuOpen = (event) => {
    setStatusMenuAnchorEl(event.currentTarget);
  };
  
  // Handle status menu close
  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
  };
  
  // Handle campaign deletion
  const handleDeleteCampaign = async () => {
    if (window.confirm(`Are you sure you want to delete ${currentCampaign.name}?`)) {
      setIsDeleting(true);
      try {
        await deleteCampaign(id);
        showSuccess(`Campaign "${currentCampaign.name}" deleted successfully`);
        navigate('/campaigns');
      } catch (error) {
        showError(`Failed to delete campaign: ${error.message}`);
        setIsDeleting(false);
      }
    }
    
    handleMenuClose();
  };
  
  // Handle status change
  const handleStatusChange = async (status) => {
    try {
      await updateCampaignStatus(id, status);
      showSuccess(`Campaign status updated to ${status}`);
    } catch (error) {
      showError(`Failed to update status: ${error.message}`);
    }
    
    handleStatusMenuClose();
  };
  
  // Calculate campaign progress
  const calculateProgress = () => {
    if (!currentCampaign || !currentCampaign.dates?.plannedStartDate || !currentCampaign.dates?.plannedEndDate) return 0;
    
    if (currentCampaign.status === 'completed' || currentCampaign.status === 'cancelled') {
      return 100;
    }
    
    const start = new Date(currentCampaign.dates.plannedStartDate).getTime();
    const end = new Date(currentCampaign.dates.plannedEndDate).getTime();
    const now = new Date().getTime();
    
    if (now <= start) return 0;
    if (now >= end) return 100;
    
    return Math.round(((now - start) / (end - start)) * 100);
  };
  
  // Loading state
  if (isLoading && !currentCampaign) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoading variant="mystical" size="large" message="Loading campaign details..." />
      </Box>
    );
  }
  
  // Error state
  if (criticalError && !currentCampaign) {
    return (
      <Box sx={{ py: 3 }}>
        <MagicCard
          elevation={0}
          sx={{ 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Error Loading Campaign
          </Typography>
          <Typography variant="body1" paragraph>
            {criticalError}
          </Typography>
          <Button
            variant="outlined"
            
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/campaigns')}
          >
            Back to Campaigns
          </Button>
        </MagicCard>
      </Box>
    );
  }
  
  // Calculate progress
  const progress = calculateProgress();
  
  // Get client and brand names
  const clientName = currentCampaign?.clientId?.name || 'Unknown Client';
  const brandName = currentCampaign?.brandId?.name || 'Unknown Brand';
  
  // Format dates
  const startDate = formatDate(currentCampaign?.dates?.plannedStartDate);
  const endDate = formatDate(currentCampaign?.dates?.plannedEndDate);
  
  // Get active platforms
  const activePlatforms = Object.entries(currentCampaign?.platforms || {})
    .filter(([key, value]) => value === true && key !== 'other')
    .map(([key]) => key);
  
  // Get campaign type
  const campaignType = currentCampaign?.type?.charAt(0)?.toUpperCase() + 
    currentCampaign?.type?.slice(1).replace('_', ' ') || 'Other';
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Critical Error Message - Above content, not replacing it */}
      {criticalError && (
        <Box sx={{ mb: 3 }}>
          <MagicCard
            elevation={0}
            sx={{ 
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              p: 3,
              border: `1px solid ${theme.palette.error.main}`,
            }}
          >
            <Typography variant="h6" gutterBottom>Error Loading Campaign</Typography>
            <Typography variant="body1" paragraph>{criticalError}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => fetchCampaign(id)}
              >
                Retry
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/campaigns')}
              >
                Back to Campaigns
              </Button>
            </Box>
          </MagicCard>
        </Box>
      )}

      {/* Only show content if we have a campaign */}
      {currentCampaign ? (
        <>
          {/* PageHeader with standardized breadcrumbs */}
          <PageHeader
            title={currentCampaign.name}
            subtitle={`${clientName} • ${brandName} • ${startDate} - ${endDate}`}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard' },
              { label: 'Campaigns', path: '/campaigns' },
              { label: currentCampaign.name }
            ]}
            actions={
              <>
                {canUpdate && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/campaigns/${id}/edit`)}
                  >
                    Edit Campaign
                  </Button>
                )}
                <IconButton
                  onClick={handleMenuOpen}
                  size="small"
                  aria-label="more actions"
                >
                  <MoreVertIcon />
                </IconButton>
              </>
            }
          />

      {/* Header with campaign info and actions */}
      <MagicCard 
        magical={true} 
        level={2}
        sx={{ 
          mb: 4,
          borderTop: `4px solid ${
            currentCampaign.status === 'active' ? theme.palette.success.main :
            currentCampaign.status === 'paused' ? theme.palette.warning.main :
            currentCampaign.status === 'completed' ? theme.palette.primary.main :
            theme.palette.divider
          }`,
        }}
      >
        <CardContent>
          <Grid container spacing={3}>
            {/* Basic info */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mr: 2 }}>
                  {currentCampaign.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusChip status={currentCampaign.status} />
                  
                  {/* Magical indicator if applicable */}
                  {currentCampaign.magicalElements?.enchantmentLevel && (
                    <Tooltip title={`Magical level: ${currentCampaign.magicalElements.enchantmentLevel}`}>
                      <MagicIcon 
                        sx={{ 
                          color: theme.palette?.accent || '#FFD700',
                          animation: 'pulse 2s infinite ease-in-out',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 0.7, transform: 'scale(1)' },
                            '50%': { opacity: 1, transform: 'scale(1.2)' },
                          },
                        }} 
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Client:</strong> {clientName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Brand:</strong> {brandName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Type:</strong> {campaignType}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Start:</strong> {startDate}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>End:</strong> {endDate}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Duration:</strong> {currentCampaign?.dates?.plannedStartDate && currentCampaign?.dates?.plannedEndDate ? Math.ceil((new Date(currentCampaign.dates.plannedEndDate) - new Date(currentCampaign.dates.plannedStartDate)) / (1000 * 60 * 60 * 24)) : 0} days
                  </Typography>
                </Grid>
              </Grid>
              
              {/* Campaign description */}
              {currentCampaign.description && (
                <Typography variant="body1" paragraph>
                  {currentCampaign.description}
                </Typography>
              )}
              
              {/* Budget overview */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Budget: {formatCurrency(currentCampaign.budget?.total || 0, currentCampaign.budget?.currency)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 100 }}>
                    {formatCurrency(currentCampaign.budget?.spent || 0, currentCampaign.budget?.currency)} spent
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={currentCampaign.budget?.total ? (currentCampaign.budget.spent / currentCampaign.budget.total) * 100 : 0} 
                    sx={{ flexGrow: 1, mx: 2, height: 6, borderRadius: 1 }}
                  />
                  <Typography variant="body2">
                    {Math.round(currentCampaign.budget?.total ? (currentCampaign.budget.spent / currentCampaign.budget.total) * 100 : 0)}%
                  </Typography>
                </Box>
              </Box>
              
              {/* Campaign progress */}
              <Box>
                <Typography variant="subtitle2">
                  Campaign Progress: {progress}%
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress} 
                    color={
                      currentCampaign.status === 'active' ? 'success' :
                      currentCampaign.status === 'paused' ? 'warning' :
                      currentCampaign.status === 'completed' ? 'primary' : 'info'
                    }
                    sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </Grid>
            
            {/* Action buttons */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
                {canUpdate && (
                  <Button
                    variant="outlined"
                    
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/campaigns/${id}/edit`)}
                  >
                    Edit
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  
                  startIcon={<AnalyticsIcon />}
                  onClick={() => navigate(`/analytics/campaigns/${id}`)}
                >
                  Analytics
                </Button>
                
                {canUpdate && (
                  <Button
                    variant="outlined"
                    onClick={handleStatusMenuOpen}
                    color={
                      currentCampaign.status === 'active' ? 'success' :
                      currentCampaign.status === 'paused' ? 'warning' :
                      currentCampaign.status === 'completed' ? 'primary' : 'primary'
                    }
                  >
                    Change Status
                  </Button>
                )}
                
                <IconButton
                  onClick={handleMenuOpen}
                  sx={{ 
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
              
              {/* Platform badges */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                justifyContent: { xs: 'flex-start', md: 'flex-end' } 
              }}>
                {activePlatforms.map((platform) => (
                  <Chip
                    key={platform}
                    icon={<PlatformIcon platform={platform} />}
                    label={platform?.charAt(0)?.toUpperCase() + platform?.slice(1)}
                    variant="outlined"
                    size="small"
                  />
                ))}
                {currentCampaign.platforms?.other?.map((platform) => (
                  <Chip
                    key={platform}
                    label={platform}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
              
              {/* Status Menu */}
              <Menu
                anchorEl={statusMenuAnchorEl}
                open={Boolean(statusMenuAnchorEl)}
                onClose={handleStatusMenuClose}
              >
                <MenuItem onClick={() => handleStatusChange('draft')}>
                  <ListItemIcon>
                    <Chip label="Draft" size="small" />
                  </ListItemIcon>
                  Set as Draft
                </MenuItem>
                <MenuItem onClick={() => handleStatusChange('planning')}>
                  <ListItemIcon>
                    <Chip label="Planning" color="info" size="small" />
                  </ListItemIcon>
                  Set as Planning
                </MenuItem>
                <MenuItem onClick={() => handleStatusChange('active')}>
                  <ListItemIcon>
                    <Chip label="Active" color="success" size="small" />
                  </ListItemIcon>
                  Set as Active
                </MenuItem>
                <MenuItem onClick={() => handleStatusChange('paused')}>
                  <ListItemIcon>
                    <Chip label="Paused" color="warning" size="small" />
                  </ListItemIcon>
                  Set as Paused
                </MenuItem>
                <MenuItem onClick={() => handleStatusChange('completed')}>
                  <ListItemIcon>
                    <Chip label="Completed"  size="small" />
                  </ListItemIcon>
                  Set as Completed
                </MenuItem>
                <MenuItem onClick={() => handleStatusChange('cancelled')}>
                  <ListItemIcon>
                    <Chip label="Cancelled" color="error" size="small" />
                  </ListItemIcon>
                  Set as Cancelled
                </MenuItem>
              </Menu>
              
              {/* Actions Menu */}
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => {
                  handleMenuClose();
                  navigate(`/campaigns/${id}/content/create`);
                }}>
                  <ListItemIcon>
                    <ContentIcon fontSize="small" />
                  </ListItemIcon>
                  Create Content
                </MenuItem>
                
                <MenuItem onClick={() => {
                  handleMenuClose();
                  navigate(`/campaigns/${id}/milestones/create`);
                }}>
                  <ListItemIcon>
                    <MilestoneIcon fontSize="small" />
                  </ListItemIcon>
                  Add Milestone
                </MenuItem>
                
                <Divider />
                
                {canDelete && (
                  <MenuItem 
                    onClick={handleDeleteCampaign}
                    disabled={isDeleting}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Typography color="error">Delete Campaign</Typography>
                  </MenuItem>
                )}
              </Menu>
            </Grid>
          </Grid>
        </CardContent>
      </MagicCard>
      
      {/* Tabs for campaign details */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="campaign details tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.secondary.main,
              height: 3,
              borderRadius: '2px 2px 0 0',
              boxShadow: `0 0 8px ${theme.palette.secondary.main}`
            }
          }}
        >
          <Tab
            icon={<CampaignIcon />}
            iconPosition="start"
            label="Overview"
            id="campaign-tab-0"
            aria-controls="campaign-tabpanel-0"
            sx={{
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.secondary.main,
              }
            }}
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Target Audience"
            id="campaign-tab-1"
            aria-controls="campaign-tabpanel-1"
            sx={{
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.secondary.main,
              }
            }}
          />
          <Tab
            icon={<MoneyIcon />}
            iconPosition="start"
            label="Budget"
            id="campaign-tab-2"
            aria-controls="campaign-tabpanel-2"
            sx={{
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.secondary.main,
              }
            }}
          />
          <Tab
            icon={<ContentIcon />}
            iconPosition="start"
            label="Content"
            id="campaign-tab-3"
            aria-controls="campaign-tabpanel-3"
            sx={{
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.secondary.main,
              }
            }}
          />
          <Tab
            icon={<CalendarIcon />}
            iconPosition="start"
            label="Milestones"
            id="campaign-tab-4"
            aria-controls="campaign-tabpanel-4"
            sx={{
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.secondary.main,
              }
            }}
          />
          <Tab
            icon={<AnalyticsIcon />}
            iconPosition="start"
            label="KPIs"
            id="campaign-tab-5"
            aria-controls="campaign-tabpanel-5"
            sx={{
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.secondary.main,
              }
            }}
          />
        </Tabs>
      </Box>
      
      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Campaign Summary */}
          <Grid item xs={12} md={6}>
            <MagicCard title="Campaign Summary" headerProps={{ avatar: <CampaignIcon /> }}>
              <CardContent>
                <Typography variant="body1" paragraph>
                  {currentCampaign.description || 'No detailed description provided.'}
                </Typography>
                
                {currentCampaign.brief && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Brief:
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <Typography variant="body2">
                        {currentCampaign.brief}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </MagicCard>
          </Grid>
          
          {/* Campaign Team */}
          <Grid item xs={12} md={6}>
            <MagicCard title="Campaign Team" headerProps={{ avatar: <PeopleIcon /> }}>
              <CardContent>
                {currentCampaign.team && currentCampaign.team.length > 0 ? (
                  <Box>
                    {currentCampaign.team.map((member, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body1">
                          {member.user?.firstName || 'Unnamed member'}
                        </Typography>
                        <Chip
                          label={member.role?.charAt(0)?.toUpperCase() + member.role?.slice(1)}
                          size="small"
                          sx={{ ml: 1 }}
                          color={
                            member.role === 'manager' ? 'primary' :
                            member.role === 'approver' ? 'success' :
                            member.role === 'creator' ? 'info' :
                            'default'
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="textSecondary">
                    No team members assigned to this campaign.
                  </Typography>
                )}
                
                {canUpdate && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/campaigns/${id}/team/edit`)}
                  >
                    Manage Team
                  </Button>
                )}
              </CardContent>
            </MagicCard>
          </Grid>
          
          {/* Magical Elements (if present) */}
          {currentCampaign.magicalElements?.enchantmentLevel && (
            <Grid item xs={12}>
              <MagicCard 
                title="Magical Elements" 
                magical={true} 
                level={2}
                headerProps={{ 
                  avatar: <MagicIcon sx={{ color: theme.palette?.accent || '#FFD700' }} /> 
                }}
              >
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Enchantment Level:
                      </Typography>
                      <Chip 
                        label={currentCampaign.magicalElements.enchantmentLevel?.charAt(0)?.toUpperCase() + currentCampaign.magicalElements.enchantmentLevel?.slice(1)} 
                        sx={{ 
                          bgcolor: alpha(theme.palette?.accent || '#FFD700', 0.1),
                          color: theme.palette?.accent || '#FFD700',
                          fontWeight: 500,
                        }}
                      />
                    </Grid>
                    
                    {currentCampaign.magicalElements.specialFeatures && 
                     currentCampaign.magicalElements.specialFeatures.length > 0 && (
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle2" gutterBottom>
                          Special Features:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentCampaign.magicalElements.specialFeatures.map((feature, index) => (
                            <Chip 
                              key={index} 
                              label={feature} 
                              icon={<MagicIcon fontSize="small" />}
                              variant="outlined"
                              sx={{ 
                                borderColor: alpha(theme.palette?.accent || '#FFD700', 0.3),
                                color: theme.palette.text.primary,
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                    
                    {currentCampaign.magicalElements.transformationNarrative && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Transformation Narrative:
                        </Typography>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            borderColor: alpha(theme.palette?.accent || '#FFD700', 0.3),
                            bgcolor: alpha(theme.palette?.accent || '#FFD700', 0.05),
                          }}
                        >
                          <Typography variant="body2">
                            {currentCampaign.magicalElements.transformationNarrative}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </MagicCard>
            </Grid>
          )}
        </Grid>
      </TabPanel>
      
      {/* Target Audience Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MagicCard title="Demographics" headerProps={{ avatar: <PeopleIcon /> }}>
              <CardContent>
                {currentCampaign.targetAudience?.demographics ? (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      <strong>Age Range:</strong> {currentCampaign.targetAudience.demographics.ageRange?.min || 18} - {currentCampaign.targetAudience.demographics.ageRange?.max || 65}
                    </Typography>
                    
                    <Typography variant="body2" gutterBottom>
                      <strong>Gender:</strong> {
                        currentCampaign.targetAudience.demographics.gender === 'all' ? 'All Genders' : 
                        currentCampaign.targetAudience.demographics.gender === 'male' ? 'Male' : 
                        currentCampaign.targetAudience.demographics.gender === 'female' ? 'Female' : 
                        'All Genders'
                      }
                    </Typography>
                    
                    {currentCampaign.targetAudience.demographics.locations && 
                     currentCampaign.targetAudience.demographics.locations.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Locations:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentCampaign.targetAudience.demographics.locations.map((location, index) => (
                            <Chip 
                              key={index} 
                              label={location} 
                              size="small" 
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {currentCampaign.targetAudience.demographics.languages && 
                     currentCampaign.targetAudience.demographics.languages.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Languages:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentCampaign.targetAudience.demographics.languages.map((language, index) => (
                            <Chip 
                              key={index} 
                              label={language === 'en' ? 'English' : 
                                     language === 'ar' ? 'Arabic' :
                                     language === 'fr' ? 'French' : language} 
                              size="small" 
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography color="textSecondary">
                    No demographic information defined.
                  </Typography>
                )}
              </CardContent>
            </MagicCard>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <MagicCard title="Psychographics" headerProps={{ avatar: <PeopleIcon /> }}>
              <CardContent>
                {currentCampaign.targetAudience?.psychographics ? (
                  <Box>
                    {currentCampaign.targetAudience.psychographics.interests && 
                     currentCampaign.targetAudience.psychographics.interests.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Interests:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentCampaign.targetAudience.psychographics.interests.map((interest, index) => (
                            <Chip 
                              key={index} 
                              label={interest} 
                              size="small" 
                              
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {currentCampaign.targetAudience.psychographics.behaviors && 
                     currentCampaign.targetAudience.psychographics.behaviors.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Behaviors:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentCampaign.targetAudience.psychographics.behaviors.map((behavior, index) => (
                            <Chip 
                              key={index} 
                              label={behavior} 
                              size="small" 
                              color="info"
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {currentCampaign.targetAudience.psychographics.values && 
                     currentCampaign.targetAudience.psychographics.values.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Values:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentCampaign.targetAudience.psychographics.values.map((value, index) => (
                            <Chip 
                              key={index} 
                              label={value} 
                              size="small" 
                              color="secondary"
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {currentCampaign.targetAudience.psychographics.lifestyleFactors && 
                     currentCampaign.targetAudience.psychographics.lifestyleFactors.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Lifestyle Factors:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {currentCampaign.targetAudience.psychographics.lifestyleFactors.map((factor, index) => (
                            <Chip 
                              key={index} 
                              label={factor} 
                              size="small" 
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography color="textSecondary">
                    No psychographic information defined.
                  </Typography>
                )}
              </CardContent>
            </MagicCard>
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Budget Tab */}
      <TabPanel value={tabValue} index={2}>
        <CampaignBudgetTab campaign={currentCampaign}/>
      </TabPanel>
      
      {/* Content Tab */}
      <TabPanel value={tabValue} index={3}>
        <CampaignContentTab campaign={currentCampaign}/>
      </TabPanel>
      
      {/* Milestones Tab */}
      <TabPanel value={tabValue} index={4}>
        <CampaignMilestoneTab campaign={currentCampaign}/>
      </TabPanel>
      
          {/* KPIs Tab */}
          <TabPanel value={tabValue} index={5}>
            <CampaignKPIsTab campaign={currentCampaign}/>
          </TabPanel>
        </>
      ) : (
        /* Show DataStateHandler when no campaign is loaded */
        <DataStateHandler 
          isLoading={isLoading} 
          error={criticalError && !currentCampaign}
          onRetry={() => fetchCampaign(id)}
          emptyMessage="Campaign not found"
          errorMessage={criticalError}
        />
      )}
    </Box>
  );
};

export default CampaignDetail;